import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Radio, Hexagon, AlertTriangle, Globe2, Sparkles } from "lucide-react";

export default function VerifyCertificate() {
  const { cid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verify/cert/${cid}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setErr("");
      })
      .catch(() => {
        if (cancelled) return;
        setErr("Certificate not found · Certidão não encontrada");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [cid]);

  const status = data?.certificate_status;
  const isValid = status === "VALID CERTIFICATE";
  const isRevoked = status === "REVOKED";
  const statusColor = isRevoked ? "#FF6FA0" : (isValid ? "#62FFB0" : "#FFD27A");

  return (
    <div className="min-h-screen bg-[#03040B] text-white grid-bg p-8 lg:p-12" data-testid="verify-cert-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="label-overline flex items-center gap-2">
              <ShieldCheck size={12} /> Public Certificate Verification · Verificação Pública
            </div>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mt-2">
              INTERGALACTIC<br />
              <span className="text-[#FFD27A]">TRANSMISSION CERTIFICATE</span>
            </h1>
            <div className="font-mono-data text-[11px] tracking-[0.2em] uppercase text-white/55 mt-2">
              S2S MISSION OPS · CERTIDÃO INTERGALÁCTICA DE TRANSMISSÃO
            </div>
          </div>
          <div className="hidden lg:flex flex-col items-center">
            <Hexagon size={64} className="text-[#FFD27A]" strokeWidth={1} />
            <div className="font-mono-data text-[9px] tracking-[0.2em] uppercase text-[#FFD27A] mt-2">
              S2S SEAL
            </div>
          </div>
        </div>

        {loading && (
          <div className="holo-panel p-12 text-center" data-testid="verify-loading">
            <Radio size={32} className="text-[#00D4FF] animate-pulse mx-auto" />
            <div className="font-mono-data text-[12px] tracking-[0.2em] uppercase text-white/55 mt-4">
              Verifying certificate…
            </div>
          </div>
        )}

        {err && (
          <div className="holo-panel p-12 text-center border-[#FF6FA0]/40" data-testid="verify-error">
            <AlertTriangle size={32} className="text-[#FF6FA0] mx-auto" />
            <div className="font-display text-2xl font-black mt-4 text-[#FF6FA0]">{err}</div>
          </div>
        )}

        {data && (
          <>
            {/* Big status badge */}
            <div className="holo-panel p-8 mb-6 relative overflow-hidden" data-testid="verify-status-panel"
                 style={{ borderColor: `${statusColor}40` }}>
              <div className="absolute inset-x-0 top-0 h-px"
                   style={{ background: `linear-gradient(90deg, transparent, ${statusColor}, transparent)` }} />
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="label-overline" style={{ color: statusColor }}>Certificate Status · Status da Certidão</div>
                  <div className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-2" data-testid="verify-status"
                       style={{ color: statusColor }}>
                    {status}
                  </div>
                  {!isRevoked && (
                    <div className="font-mono-data text-[11px] tracking-[0.2em] uppercase text-[#62FFB0] mt-2 flex items-center gap-2"
                         data-testid="verify-signal-status">
                      <Radio size={12} className="blink" /> {data.signal_status}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="label-overline">CERTIFICATE ID</div>
                  <div className="font-mono-data text-sm text-[#FFD27A] mt-1">{data.certificate_id}</div>
                </div>
              </div>
            </div>

            {/* Mission grid */}
            <div className="grid lg:grid-cols-2 gap-4 mb-6">
              <Tile label="Operator · Operador" value={data.client_name_anonymized} icon={Sparkles} testid="verify-operator" />
              <Tile label="Mission ID · ID da Missão" value={data.mission_id} testid="verify-mission-id" />
              <Tile label="Transmission ID" value={data.transmission_id} mono testid="verify-tx-id" />
              <Tile label="Launched · Lançamento (UTC)" value={data.started_at?.slice(0, 19).replace("T", " ")} testid="verify-launched" />
            </div>

            {/* Destination */}
            <div className="holo-panel p-6 mb-6" data-testid="verify-destination">
              <div className="label-overline flex items-center gap-2">
                <Globe2 size={12} /> Destination · Destino
              </div>
              <div className="flex items-center gap-6 mt-3 flex-wrap">
                <div className="w-16 h-16 rounded-full shrink-0"
                     style={{ background: data.destination?.color || "#A077FF",
                              boxShadow: `0 0 30px ${data.destination?.color || "#A077FF"}66` }} />
                <div className="flex-1">
                  <div className="font-display font-black text-2xl tracking-tighter">
                    {data.destination?.name || "—"}
                  </div>
                  <div className="font-mono-data text-[11px] tracking-[0.18em] uppercase text-white/55 mt-1">
                    {data.destination?.constellation || "Solar System · Sol"}
                    {" · "}
                    {data.destination?.distance_ly
                      ? `${data.destination.distance_ly} ly`
                      : (data.destination?.distance_au
                         ? `${data.destination.distance_au} AU`
                         : "within Solar System")}
                  </div>
                </div>
              </div>
            </div>

            {/* Digital hash */}
            <div className="holo-panel p-6 mb-6" data-testid="verify-hash">
              <div className="label-overline">SHA-512 Digital Hash · Hash Digital</div>
              <div className="font-mono-data text-[10px] text-[#00D4FF] mt-2 break-all leading-relaxed">
                {data.digital_hash}
              </div>
            </div>

            {/* Operator notes / partner confirmation */}
            {(data.operator_notes || data.satellite_partner_confirmation) && (
              <div className="holo-panel p-6 mb-6" data-testid="verify-notes">
                <div className="label-overline">Mission Operations Notes</div>
                {data.satellite_partner_confirmation && (
                  <div className="mt-3">
                    <div className="font-mono-data text-[10px] tracking-[0.2em] uppercase text-[#62FFB0]">
                      Satellite Partner Confirmation
                    </div>
                    <div className="text-sm mt-1 text-white/85">{data.satellite_partner_confirmation}</div>
                  </div>
                )}
                {data.operator_notes && (
                  <div className="mt-3">
                    <div className="font-mono-data text-[10px] tracking-[0.2em] uppercase text-[#00D4FF]">
                      Operator Notes
                    </div>
                    <div className="text-sm mt-1 text-white/85 italic">{data.operator_notes}</div>
                  </div>
                )}
              </div>
            )}

            <div className="font-mono-data text-[10px] tracking-[0.2em] uppercase text-white/45 text-center mt-8">
              S2S Mission Operations · Amazon Kuiper Optical Relay<br />
              This certificate confirms simulated interstellar transmission registration. Real signal transmission depends on partner confirmation.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, icon: Icon, mono, testid }) {
  return (
    <div className="holo-panel p-4" data-testid={testid}>
      <div className="label-overline flex items-center gap-2">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className={`${mono ? "font-mono-data text-xs" : "font-display text-lg font-bold tracking-tight"} text-white mt-1.5 break-all`}>
        {value ?? "—"}
      </div>
    </div>
  );
}
